import { subscribeWebSocketChannel } from '@/lib/console-websocket';

export type MemberImportRealtimeEvent =
  | {
      type: 'started';
      importId: string;
      total: number;
    }
  | {
      type: 'progress';
      importId: string;
      processed: number;
      total: number;
      index: number;
      ok: boolean;
      memberId?: string;
      error?: string;
    }
  | {
      type: 'complete';
      importId: string;
      processed: number;
      total: number;
      succeeded: number;
      failed: number;
    }
  | {
      type: 'failed';
      importId: string;
      code: string;
    };

export const memberImportUserChannel = (userId: string) => `people:import:user:${userId}`;

const isMemberImportEvent = (payload: unknown): payload is MemberImportRealtimeEvent =>
  typeof payload === 'object' &&
  payload !== null &&
  'type' in payload &&
  typeof (payload as { type?: unknown }).type === 'string';

export const subscribeMemberImportEvents = (
  userId: string,
  importId: string,
  handler: (event: MemberImportRealtimeEvent) => void,
): (() => void) =>
  subscribeWebSocketChannel(memberImportUserChannel(userId), (payload) => {
    if (!isMemberImportEvent(payload) || payload.importId !== importId) return;
    handler(payload);
  });

export interface MemberImportProgress {
  processed: number;
  total: number;
  succeeded: number;
  failed: number;
}

export const progressFromImportEvent = (
  event: MemberImportRealtimeEvent,
  previous: MemberImportProgress | null,
): MemberImportProgress | null => {
  if (event.type === 'started') {
    return { processed: 0, total: event.total, succeeded: 0, failed: 0 };
  }
  if (event.type === 'progress') {
    return {
      processed: event.processed,
      total: event.total,
      succeeded: (previous?.succeeded ?? 0) + (event.ok ? 1 : 0),
      failed: (previous?.failed ?? 0) + (event.ok ? 0 : 1),
    };
  }
  if (event.type === 'complete') {
    return {
      processed: event.processed,
      total: event.total,
      succeeded: event.succeeded,
      failed: event.failed,
    };
  }
  return previous;
};
